import useGenreOptions from "../hooks/useGenreOptions";
// src/pages/Home.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  IconButton,
  Typography,
  Box,
  Chip,
  Stack,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  CardActions,
  Grid,
  Container,
  Skeleton,
  Tabs,
  Tab,
  Tooltip,
  Paper,
  useMediaQuery,
  Drawer,
  Autocomplete,
  TextareaAutosize,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import StadiumIcon from "@mui/icons-material/Stadium";
import MicExternalOnIcon from "@mui/icons-material/MicExternalOn";
import VideocamIcon from "@mui/icons-material/Videocam";
import SendIcon from "@mui/icons-material/Send";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShareIcon from "@mui/icons-material/Share";
import ReportIcon from "@mui/icons-material/Report";
import { fetchPopulatArtists } from "../services/artist.service";
import { directoryService, eventService } from "../services/resources.service";
import { FilterButton, ViewModeToggle } from "../components/DiscoveryActions";
import DiscoveryMap from "../components/DiscoveryMap";
import { getHomeDirectoryQuery, getHomeEventQuery, readHomeFilterState, writeHomeFilterState } from "../services/homeDiscovery";
import CommittedRadiusSlider from "../components/CommittedRadiusSlider";
import { INFINITE_RADIUS, NEAR_ME_RADIUS_KM, radiusLabel } from "../services/radius";
import { formatDateTime, formatEventPrice, formatMoneyRange, getMessage, hasEventPrice, useLocale } from "../i18n/locale";
import { getStudioRateDisplay } from "../services/studioRates";
import LinkedAccountBadge from "../components/LinkedAccountBadge";
import { BANNER_IMAGE_FALLBACK, getBannerImage, getProfileImage, imageFallbackHandler, PROFILE_IMAGE_FALLBACK } from "../services/profileImages";
import { fetchAllMapEntities } from "../services/mapEntities";
import useDebouncedSearch from "../hooks/useDebouncedSearch";
import { fetchHomeEvents, HOME_EVENT_COUNT } from "../services/homeEvents";

const HOME_TAB_KEYS = ["events", "artists", "venues", "studios", "media"];

async function fetchVenues(params = {}) {
  return directoryService.venues({ ...params, pageSize: 12 });
}

async function fetchStudios(params = {}) {
  return directoryService.studios({ ...params, pageSize: 12 });
}

async function fetchMedia(params = {}) {
  return directoryService.media({ ...params, pageSize: 12 });
}

function useInfiniteDirectory({ active, query, fetchPage }) {
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef(null);
  const requestVersionRef = useRef(0);
  const loadingRef = useRef(false);

  useEffect(() => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    loadingRef.current = true;
    setLoading(true);
    setPage(1);
    setHasMore(true);
    setItems(null);

    fetchPage({ ...query, page: 1 })
      .then((response) => {
        if (requestVersionRef.current !== requestVersion) return;
        setItems(response.data);
        setHasMore(response.hasMore);
      })
      .finally(() => {
        if (requestVersionRef.current !== requestVersion) return;
        loadingRef.current = false;
        setLoading(false);
      });
  }, [query, fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore) return;
    const nextPage = page + 1;
    const requestVersion = requestVersionRef.current;
    loadingRef.current = true;
    setLoading(true);

    fetchPage({ ...query, page: nextPage })
      .then((response) => {
        if (requestVersionRef.current !== requestVersion) return;
        setPage(nextPage);
        setHasMore(response.hasMore);
        setItems((current) => {
          const ids = new Set((current ?? []).map((item) => item.id));
          return [...(current ?? []), ...response.data.filter((item) => !ids.has(item.id))];
        });
      })
      .finally(() => {
        if (requestVersionRef.current !== requestVersion) return;
        loadingRef.current = false;
        setLoading(false);
      });
  }, [fetchPage, hasMore, page, query]);

  useEffect(() => {
    const target = sentinelRef.current;
    if (!active || !target || !hasMore || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: "240px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [active, hasMore, loadMore]);

  return { items, hasMore, loading, loadMore, sentinelRef };
}

// ---------- Small UI pieces ----------
const RatingBadge = ({ value }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <StarRoundedIcon fontSize="small" />
    <Typography variant="body2" fontWeight={600}>
      {value?.toFixed ? value.toFixed(1) : value}
    </Typography>
  </Stack>
);

const Price = ({ event }) => {
  const locale = useLocale();
  return (
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {formatEventPrice(event, locale)}
    </Typography>
  );
};

// ---------- Cards ----------
function EventCard({ event, onClick, onSaveCalendar, onShare, onReport }) {
  const locale = useLocale();
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        width: "100%",
        overflow: "hidden",
        ":hover": { boxShadow: 2 },
      }}
    >
      <CardActionArea onClick={onClick}>
        <CardMedia
          component="img"
          height="160"
          image={event.poster || BANNER_IMAGE_FALLBACK}
          onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
          alt={`${event.title} poster`}
          loading="lazy"
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={0.75}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.4 }}
            >
              {formatDateTime(event.startAt, locale)}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                lineHeight: 1.25,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {event.title}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {event?.lineup?.map((a) => a.name).join(", ")} •{" "}
              {event?.venue?.name} ({event?.venue?.city})
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 0.5, flexWrap: "wrap", gap: 1 }}
            >
              {event.tags?.slice(0, 3).map((t) => (
                <Chip key={t} label={t} size="small" />
              ))}
              {hasEventPrice(event) && (
                <Chip
                  icon={<CalendarMonthIcon />}
                  label={<Price event={event} />}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>

      <CardActions
        sx={{
          justifyContent: "space-between",
          px: { xs: 1.5, sm: 2 },
          pb: { xs: 1.5, sm: 2 },
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<CalendarMonthIcon />}
            onClick={() => onSaveCalendar?.(event)}
          >
            Save
          </Button>
          <Button
            size="small"
            startIcon={<ShareIcon />}
            onClick={() => onShare?.(event)}
          >
            Share
          </Button>
        </Stack>
        <Tooltip title="Report event">
          <IconButton
            onClick={() => onReport?.(event)}
            aria-label="Report event"
            sx={{ width: 40, height: 40 }}
          >
            <ReportIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

function ArtistCard({ artist, onView }) {
  const localeContext = useLocale();
  const banner = getBannerImage(artist);
  const bannerAlt = getMessage(localeContext.locale, "artistBannerAlt").replace("{name}", artist.name);
  const fee = formatMoneyRange(artist.feeMin, artist.feeMax, {
    locale: localeContext.locale,
    currency: artist.currency || localeContext.currency,
  });

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, width: "100%", ":hover": { boxShadow: 2 } }}
    >
      <CardActionArea onClick={() => onView?.(artist)}>
          <CardMedia
            component="img"
            height="104"
            image={banner}
            onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
            alt={bannerAlt}
            sx={{ objectFit: "cover" }}
          />
        </CardActionArea>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar src={getProfileImage(artist)} imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }} alt={artist.name} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="subtitle1" noWrap fontWeight={700}>{artist.name}</Typography>
              <LinkedAccountBadge ownerUserId={artist.ownerUserId} />
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              {artist.city} • {artist.genres?.join(", ")}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.5, flexWrap: "wrap" }}
            >
              <RatingBadge value={artist.rating} />
              <Typography variant="body2" color="text.secondary">
                {fee}
              </Typography>
            </Stack>
          </Box>

          <IconButton
            onClick={() => onView?.(artist)}
            aria-label="View profile"
            sx={{ width: 40, height: 40 }}
          >
            <SendIcon />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
}

function VenueCard({ venue, onView }) {
  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, width: "100%", ":hover": { boxShadow: 2 } }}
    >
      <CardActionArea onClick={() => onView?.(venue)}>
        <CardMedia
          component="img"
          height="130"
          image={getBannerImage(venue)}
          onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
          alt={venue.name}
          loading="lazy"
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={getProfileImage(venue)} imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }} alt={venue.name} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>{venue.name}</Typography>
                <LinkedAccountBadge ownerUserId={venue.ownerUserId} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {venue.city} • Cap {venue.capacity}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                {venue.hasPA && <Chip size="small" label="PA" />}
                {venue.hasLights && <Chip size="small" label="Lights" />}
                <Chip
                  size="small"
                  icon={<StarRoundedIcon />}
                  label={venue.rating}
                  variant="outlined"
                />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function StudioCard({ studio, onView }) {
  const { locale } = useLocale();

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, width: "100%", ":hover": { boxShadow: 2 } }}
    >
      <CardActionArea onClick={() => onView?.(studio)}>
        <CardMedia
          component="img"
          height="130"
          image={getBannerImage(studio)}
          onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
          alt={studio.name}
          loading="lazy"
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={getProfileImage(studio)} imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }} alt={studio.name} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>{studio.name}</Typography>
                <LinkedAccountBadge ownerUserId={studio.ownerUserId} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {[studio.city, getStudioRateDisplay(studio, locale)].filter(Boolean).join(" • ")}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                {studio.services?.slice(0, 3).map((s) => (
                  <Chip key={s} label={s} size="small" />
                ))}
                <Chip
                  size="small"
                  icon={<StarRoundedIcon />}
                  label={studio.rating}
                  variant="outlined"
                />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function MediaCard({ media, onView }) {
  const { locale } = useLocale();

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, width: "100%", ":hover": { boxShadow: 2 } }}
    >
      <CardActionArea onClick={() => onView?.(media)}>
        <CardMedia
          component="img"
          height="130"
          image={getBannerImage(media)}
          onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
          alt={media.name}
          loading="lazy"
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={getProfileImage(media)} imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }} alt={media.name} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>{media.name}</Typography>
                <LinkedAccountBadge ownerUserId={media.ownerUserId} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {[media.city, getStudioRateDisplay(media, locale)].filter(Boolean).join(" • ")}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                {media.services?.slice(0, 3).map((service) => (
                  <Chip key={service} label={service} size="small" />
                ))}
                {media.rating != null && (
                  <Chip
                    size="small"
                    icon={<StarRoundedIcon />}
                    label={media.rating}
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ---------- Section Headers ----------
function SectionHeader({ icon, title, actionText, onAction }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: { xs: 1, sm: 1.5 }, minWidth: 0 }}
    >
      <Stack direction="row" spacing={{ xs: 0.75, sm: 1.2 }} alignItems="center" minWidth={0}>
        <Box sx={{ display: "flex", "& svg": { fontSize: { xs: 20, sm: 24 } } }}>{icon}</Box>
        <Typography variant="h6" fontWeight={800} noWrap sx={{ fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
          {title}
        </Typography>
      </Stack>
      {actionText && (
        <Button size="small" onClick={onAction} sx={{ flexShrink: 0, px: { xs: 1, sm: 2.25 } }}>
          {actionText}
        </Button>
      )}
    </Stack>
  );
}

// ---------- Main Home ----------
export default function Home({
  onNavigate,
  user,
  region,
  onChangeRegion,
  discoveryLocation,
  discoveryRadiusKm,
  onChangeDiscoveryRadius,
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const { locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = HOME_TAB_KEYS.indexOf(searchParams.get("tab"));
  const tab = requestedTab >= 0 ? requestedTab : 0;
  const initialFilters = useRef(readHomeFilterState(searchParams, {
    locationChip: region || "Anywhere",
    radius: discoveryRadiusKm,
  })).current;
  const { inputValue: searchInput, setInputValue: setSearchInput, searchValue: query, submitSearch } = useDebouncedSearch();
  const [locationChip, setLocationChip] = useState(initialFilters.locationChip);
  const [mapMode, setMapMode] = useState(false);
  const previousRegionRef = useRef(region);

  useEffect(() => {
    if (previousRegionRef.current === region) return;
    previousRegionRef.current = region;
    setLocationChip(region || "Anywhere");
  }, [region]);

  const [eventList, setEventList] = useState(null);
  const [mapEntities, setMapEntities] = useState({
    events: [], artists: [], venues: [], studios: [], media: [],
  });
  const [popularArtistList, setPopularArtistList] = useState(null);
  const [artistList, setArtistList] = useState(null);
  const [artistPage, setArtistPage] = useState(1);
  const [artistsHaveMore, setArtistsHaveMore] = useState(true);
  const [artistsLoading, setArtistsLoading] = useState(false);
  const artistLoadMoreRef = useRef(null);
  const artistRequestVersionRef = useRef(0);
  const artistLoadInFlightRef = useRef(false);

  // Filters (MVP)
  const [dateFilter, setDateFilter] = useState(initialFilters.dateFilter); // Today | Weekend | All
  const radius = discoveryRadiusKm;
  const setRadius = onChangeDiscoveryRadius;
  const genreOptions = useGenreOptions();
  const [genres, setGenres] = useState(initialFilters.genres);
  const [accessibleOnly, setAccessibleOnly] = useState(initialFilters.accessibleOnly);

  useEffect(() => {
    const next = writeHomeFilterState(searchParams, {
      locationChip, dateFilter, radius, genres, accessibleOnly,
    }, {
      locationChip: region || "Anywhere",
      radius: discoveryRadiusKm,
    });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, locationChip, dateFilter, radius, genres, accessibleOnly, region, discoveryRadiusKm]);

  // Drawer state
  const [filtersOpen, setFiltersOpen] = useState(false);

  const eventQuery = useMemo(
    () =>
      getHomeEventQuery({
        dateFilter,
        radius,
        genres,
        query,
        accessibleOnly,
        locationChip,
        discoveryLocation,
      }),
    [
      dateFilter,
      radius,
      genres,
      query,
      accessibleOnly,
      locationChip,
      discoveryLocation,
    ]
  );
  const directoryQuery = useMemo(
    () => getHomeDirectoryQuery(query, {
      locationChip,
      radiusKm: radius,
      discoveryLocation,
      genres,
      accessibleOnly,
    }),
    [
      query,
      locationChip,
      radius,
      discoveryLocation,
      genres,
      accessibleOnly,
    ]
  );
  const artistDirectoryQuery = useMemo(
    () => ({ ...directoryQuery, registeredOnly: true }),
    [directoryQuery]
  );

  useEffect(() => {
    if (!mapMode) return undefined;

    let active = true;
    const controller = new AbortController();
    const sources = [
      ["events", eventService.list, eventQuery],
      ["artists", directoryService.artists, artistDirectoryQuery],
      ["venues", directoryService.venues, directoryQuery],
      ["studios", directoryService.studios, directoryQuery],
      ["media", directoryService.media, directoryQuery],
    ];
    const [key, listEntities, mapQuery] = sources[tab];

    fetchAllMapEntities(listEntities, mapQuery, { signal: controller.signal })
      .then((items) => {
        if (active) setMapEntities((current) => ({ ...current, [key]: items }));
      })
      .catch(() => {});

    return () => {
      active = false;
      controller.abort();
    };
  }, [mapMode, tab, eventQuery, artistDirectoryQuery, directoryQuery]);

  // Load data on mount and whenever an event discovery filter changes.
  useEffect(() => {
    fetchHomeEvents(eventQuery).then(setEventList);
  }, [eventQuery]);

  useEffect(() => {
    let active = true;
    fetchPopulatArtists().then((response) => {
      if (active) setPopularArtistList(response.data);
    });
    return () => {
      active = false;
    };
  }, [directoryQuery]);

  const venuesFeed = useInfiniteDirectory({ active: tab === 2, query: directoryQuery, fetchPage: fetchVenues });
  const studiosFeed = useInfiniteDirectory({ active: tab === 3, query: directoryQuery, fetchPage: fetchStudios });
  const mediaFeed = useInfiniteDirectory({ active: tab === 4, query: directoryQuery, fetchPage: fetchMedia });
  const venueList = venuesFeed.items;
  const studioList = studiosFeed.items;
  const mediaList = mediaFeed.items;

  useEffect(() => {
    const requestVersion = artistRequestVersionRef.current + 1;
    artistRequestVersionRef.current = requestVersion;
    artistLoadInFlightRef.current = true;
    setArtistsLoading(true);
    setArtistPage(1);
    setArtistsHaveMore(true);
    setArtistList(null);

    directoryService.artists(artistDirectoryQuery)
      .then((response) => {
        if (artistRequestVersionRef.current !== requestVersion) return;
        setArtistList(response.data);
        setArtistsHaveMore(response.hasMore);
      })
      .finally(() => {
        if (artistRequestVersionRef.current !== requestVersion) return;
        artistLoadInFlightRef.current = false;
        setArtistsLoading(false);
      });
  }, [artistDirectoryQuery]);

  const loadNextArtistPage = useCallback(() => {
    if (artistLoadInFlightRef.current || !artistsHaveMore) return;

    const nextPage = artistPage + 1;
    const requestVersion = artistRequestVersionRef.current;
    artistLoadInFlightRef.current = true;
    setArtistsLoading(true);

    directoryService.artists({ ...artistDirectoryQuery, page: nextPage })
      .then((response) => {
        if (artistRequestVersionRef.current !== requestVersion) return;
        setArtistPage(nextPage);
        setArtistsHaveMore(response.hasMore);
        setArtistList((current) => {
          const existingIds = new Set((current ?? []).map((artist) => artist.id));
          return [
            ...(current ?? []),
            ...response.data.filter((artist) => !existingIds.has(artist.id)),
          ];
        });
      })
      .finally(() => {
        if (artistRequestVersionRef.current !== requestVersion) return;
        artistLoadInFlightRef.current = false;
        setArtistsLoading(false);
      });
  }, [artistPage, artistsHaveMore, artistDirectoryQuery]);

  useEffect(() => {
    const target = artistLoadMoreRef.current;
    if (
      tab !== 1 ||
      !target ||
      !artistsHaveMore ||
      typeof IntersectionObserver === "undefined"
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadNextArtistPage();
      },
      { rootMargin: "240px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [tab, artistsHaveMore, loadNextArtistPage]);

  // Handlers
  const handleLocationClick = async () => {
    if (radius !== INFINITE_RADIUS) {
      setRadius(INFINITE_RADIUS);
      return;
    }
    if (discoveryLocation) {
      setLocationChip("Near me");
      setRadius(NEAR_ME_RADIUS_KM);
      return;
    }
    const location = await onChangeRegion?.();
    if (location) {
      setLocationChip("Near me");
      setRadius(NEAR_ME_RADIUS_KM);
    }
  };

  const saveToCalendar = (evt) => {
    const start =
      new Date(evt.startAt).toISOString().replace(/[-:]/g, "").split(".")[0] +
      "Z";

    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:Band-Joe",
      "BEGIN:VEVENT",
      `UID:${evt.id}@band-joe`,
      `DTSTAMP:${start}`,
      `DTSTART:${start}`,
      `SUMMARY:${evt.title}`,
      `DESCRIPTION:${evt.lineup?.map((a) => a.name).join(", ")} at ${evt.venue?.name
      }`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${evt.title}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Derived subtitle & active filter count
  const sectionSubtitle = useMemo(() => {
    const parts = [];
    if (locationChip) parts.push(locationChip);
    if (tab === 0 && dateFilter !== "All") parts.push(dateFilter);
    if (genres.length && !genres.includes("All")) parts.push(genres.join(", "));
    if (radius !== INFINITE_RADIUS) parts.push(radiusLabel(radius));
    if (accessibleOnly) parts.push("Accessible");
    return parts.join(" • ");
  }, [locationChip, tab, dateFilter, genres, radius, accessibleOnly]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (tab === 0 && dateFilter !== "All") n++;
    if (!(genres.length === 1 && genres[0] === "All")) n++;
    if (radius !== INFINITE_RADIUS) n++;
    if (accessibleOnly) n++;
    return n;
  }, [tab, dateFilter, genres, radius, accessibleOnly]);

  const resetFilters = () => {
    setDateFilter("All");
    setGenres(["All"]);
    setRadius(INFINITE_RADIUS);
    setAccessibleOnly(false);
  };

  const selectTab = (nextTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === 0) nextParams.delete("tab");
    else nextParams.set("tab", HOME_TAB_KEYS[nextTab]);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      {/* App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {/* Search row */}
        <Box component="form" onSubmit={submitSearch} sx={{ px: { xs: 1.5, sm: 2 }, p: { xs: 1.25, sm: 2 } }}>
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            fullWidth
            placeholder={getMessage(locale, "homeSearchPlaceholder")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Primary tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => selectTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Primary sections"
          sx={{
            px: 1,
            "& .MuiTabs-indicator": { height: 3 },
            borderTop: 1,
            borderColor: "divider",
            minHeight: { xs: 40, sm: 44 },
            ".MuiTab-root": {
              minHeight: { xs: 40, sm: 44 },
              minWidth: { xs: "auto", sm: 90 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: "0.78rem", sm: "0.875rem" },
            },
            ".MuiTab-icon": { display: { xs: "none", sm: "inline-flex" } },
          }}
        >
          <Tab label="Events" icon={<CalendarMonthIcon />} iconPosition="start" />
          <Tab label="Artists" icon={<MusicNoteIcon />} iconPosition="start" />
          <Tab label="Venues" icon={<StadiumIcon />} iconPosition="start" />
          <Tab label="Studios" icon={<MicExternalOnIcon />} iconPosition="start" />
          <Tab label={getMessage(locale, "homeMediaTab")} icon={<VideocamIcon />} iconPosition="start" />
        </Tabs>
      </AppBar>

      {/* Content */}
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.25, sm: 2 } }}>
        {/* Region & ultra-compact filter bar */}
        <Stack spacing={{ xs: 0, sm: 1.5 }} sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: { xs: "nowrap", sm: "wrap" }, rowGap: 1, minWidth: 0 }}
          >
            {/* Location always visible */}
            <Chip
              icon={<LocationOnIcon />}
              label={locationChip}
              onClick={handleLocationClick}
              variant={radius === INFINITE_RADIUS ? "outlined" : "filled"}
              sx={{
                mr: { xs: 0, sm: 0.5 },
                minWidth: 0,
                maxWidth: { xs: 150, sm: "none" },
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />

            {/* Date filters apply only to events. */}
            {tab === 0 && <>
              <Chip
                label={dateFilter}
                onClick={() => setFiltersOpen(true)}
                variant="outlined"
                sx={{ display: { xs: "inline-flex", sm: "none" }, flexShrink: 0 }}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={dateFilter}
                onChange={(_, v) => v && setDateFilter(v)}
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  ml: 0.5,
                  borderRadius: 999,
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    px: 1.25,
                  },
                }}
              >
                <ToggleButton value="Today">Today</ToggleButton>
                <ToggleButton value="Weekend">Weekend</ToggleButton>
                <ToggleButton value="All">All</ToggleButton>
              </ToggleButtonGroup>
            </>}

            {/* Filters button with active-count */}
            <FilterButton
              count={activeCount}
              compactOnMobile
              onClick={() => setFiltersOpen(true)}
              aria-label="Filters"
              sx={{ ml: "auto" }}
            />

            {/* Map/List toggle */}
            <ViewModeToggle
              mapMode={mapMode}
              onChange={setMapMode}
              sx={{ display: { xs: "none", sm: "inline-flex" }, ml: { xs: 0, sm: 1 } }}
            />
          </Stack>

          {/* Subtitle with compact summary of active filters */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {tab === 0
              ? `Discover live shows${sectionSubtitle ? ` • ${sectionSubtitle}` : ""
              }`
              : tab === 1
                ? "Explore local artists and bands"
                : tab === 2
                  ? "Find venues that match your vibe & budget"
                  : tab === 3
                    ? "Studios offering recording, mixing, mastering & rehearsal"
                    : getMessage(locale, "homeMediaSubtitle")}
          </Typography>
        </Stack>

        {/* Tab Panels */}
        {tab === 0 && (
          <>
            <SectionHeader
              icon={<CalendarMonthIcon />}
              title="Upcoming near you"
              actionText="See all events"
              onAction={() => onNavigate?.("/events")}
            />

            {mapMode && (
              <Box sx={{ mb: 2 }}>
                <DiscoveryMap
                  items={mapEntities.events}
                  getCoordinates={(event) => event.venue?.geo}
                  getLabel={(event) => event.title}
                  onSelect={(event) => onNavigate?.(`/events/${event.id}`)}
                  height={280}
                />
              </Box>
            )}

            {/* <Grid container spacing={2} sx={{ mb: 3, flex: 1 }}>
              {(eventList ?? Array.from({ length: HOME_EVENT_COUNT })).map((evt, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={evt?.id ?? idx}>
                  {evt ? (
                    <EventCard
                      event={evt}
                      onClick={() => {
                        onNavigate?.(`/events/${evt.id}`);
                      }}
                      onSaveCalendar={saveToCalendar}
                      onShare={(e) => alert(`Share: ${e.title}`)}
                      onReport={(e) => alert(`Report: ${e.title}`)}
                    />
                  ) : (
                    <Skeleton
                      variant="rounded"
                      height={260}
                      sx={{ borderRadius: 3 }}
                    />
                  )}
                </Grid>
              ))}
            </Grid> */}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(eventList ?? Array.from({ length: HOME_EVENT_COUNT })).map((evt, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={evt?.id ?? idx}>
                  {evt ? (
                    <EventCard
                      sx={{ mb: 3 }}
                      event={evt}
                      onClick={() => {
                        onNavigate?.(`/events/${evt.id}`);
                      }}
                      onSaveCalendar={saveToCalendar}
                      onShare={(e) => alert(`Share: ${e.title}`)}
                      onReport={(e) => alert(`Report: ${e.title}`)}
                    />
                  ) : (
                    <Skeleton
                      variant="rounded"
                      height={260}
                      sx={{ borderRadius: 3 }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>

            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 4,
              }}
            >
              <StadiumIcon />
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={800}>Gig Board</Typography>
                <Typography variant="body2" color="text.secondary">
                  Venues post open calls; artists apply with EPK. Shortlist and
                  award in-app.
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => onNavigate?.("/events/gigs")}
              >
                View gigs
              </Button>
            </Paper>

            <SectionHeader
              icon={<MusicNoteIcon />}
              title="Popular artists this week"
              actionText="Browse artists"
              onAction={() => onNavigate?.("/artists")}
            />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(popularArtistList ?? Array.from({ length: 2 })).map((a, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={a?.id ?? idx}>
                  {a ? (
                    <ArtistCard
                      artist={a}
                      onView={(x) => onNavigate?.(`/artists/${x.id}`)}
                    />
                  ) : (
                    <Skeleton
                      variant="rounded"
                      height={120}
                      sx={{ borderRadius: 3 }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>

            <SectionHeader
              icon={<StadiumIcon />}
              title="Featured venues"
              actionText="All venues"
              onAction={() => onNavigate?.("/venues")}
            />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(venueList ?? Array.from({ length: 2 })).map((v, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={v?.id ?? idx}>
                  {v ? (
                    <VenueCard
                      venue={v}
                      onView={(x) => onNavigate?.(`/venues/${x.id}`)}
                    />
                  ) : (
                    <Skeleton
                      variant="rounded"
                      height={220}
                      sx={{ borderRadius: 3 }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>

            <SectionHeader
              icon={<MicExternalOnIcon />}
              title="Studios nearby"
              actionText="All studios"
              onAction={() => onNavigate?.("/studios")}
            />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(studioList ?? Array.from({ length: 2 })).map((s, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s?.id ?? idx}>
                  {s ? (
                    <StudioCard
                      studio={s}
                      onView={(x) => onNavigate?.(`/studios/${x.id}`)}
                    />
                  ) : (
                    <Skeleton
                      variant="rounded"
                      height={220}
                      sx={{ borderRadius: 3 }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {tab === 1 && (
          <>
            <SectionHeader
              icon={<MusicNoteIcon />}
              title="Artists"
              actionText="Advanced filters"
              onAction={() => onNavigate?.("/artists?filters=1")}
            />
            {mapMode && (
              <Box sx={{ mb: 2 }}>
                <DiscoveryMap
                  items={mapEntities.artists}
                  getCoordinates={(artist) => artist.geo}
                  getLabel={(artist) => artist.name}
                  onSelect={(artist) => onNavigate?.(`/artists/${artist.id}`)}
                  labelMessageKey="artistMapLabel"
                  height={280}
                />
              </Box>
            )}
            <Grid container spacing={2}>
              {(artistList ?? Array.from({ length: 4 })).map((a, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={a?.id ?? idx}>
                  {a ? (
                    <ArtistCard
                      artist={a}
                      onView={(x) => onNavigate?.(`/artists/${x.id}`)}
                    />
                  ) : (
                    <Skeleton
                      variant="rounded"
                      height={120}
                      sx={{ borderRadius: 3 }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
            {artistsHaveMore && (
              <Box ref={artistLoadMoreRef} sx={{ minHeight: 48, mt: 2 }}>
                {artistsLoading && (
                  <Grid container spacing={2}>
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={idx}>
                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </>
        )}

        {tab === 2 && (
          <>
            <SectionHeader
              icon={<StadiumIcon />}
              title="Venues"
              actionText="Post a gig"
              onAction={() => onNavigate?.("/venues/gig/new")}
            />
            {mapMode && (
              <Box sx={{ mb: 2 }}>
                <DiscoveryMap
                  items={mapEntities.venues}
                  getCoordinates={(venue) => venue.geo}
                  getLabel={(venue) => venue.name}
                  onSelect={(venue) => onNavigate?.(`/venues/${venue.id}`)}
                  labelMessageKey="venueMapLabel"
                  height={280}
                />
              </Box>
            )}
            <Grid container spacing={2}>
              {(venueList ?? Array.from({ length: 4 })).map((v, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={v?.id ?? idx}>
                  {v ? (
                    <VenueCard
                      venue={v}
                      onView={(x) => onNavigate?.(`/venues/${x.id}`)}
                    />
                  ) : (
                    <Skeleton
                      variant="rounded"
                      height={220}
                      sx={{ borderRadius: 3 }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
            {venuesFeed.hasMore && (
              <Box ref={venuesFeed.sentinelRef} sx={{ py: 2, textAlign: "center" }}>
                <Button onClick={venuesFeed.loadMore} disabled={venuesFeed.loading}>
                  {getMessage(locale, "loadingContent")}
                </Button>
              </Box>
            )}
          </>
        )}

        {tab === 3 && (
          <>
            <SectionHeader
              icon={<MicExternalOnIcon />}
              title="Studios"
              actionText="Offer a bundle"
              onAction={() => onNavigate?.("/studios/bundles/new")}
            />
            {mapMode && (
              <Box sx={{ mb: 2 }}>
                <DiscoveryMap
                  items={mapEntities.studios}
                  getCoordinates={(studio) => studio.geo}
                  getLabel={(studio) => studio.name}
                  onSelect={(studio) => onNavigate?.(`/studios/${studio.id}`)}
                  labelMessageKey="studioMapLabel"
                  height={280}
                />
              </Box>
            )}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(studioList ?? Array.from({ length: 4 })).map((s, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={s?.id ?? idx}>
                  {s ? (
                    <StudioCard
                      studio={s}
                      onView={(x) => onNavigate?.(`/studios/${x.id}`)}
                    />
                  ) : (
                    <Skeleton
                      variant="rounded"
                      height={220}
                      sx={{ borderRadius: 3 }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
            {studiosFeed.hasMore && (
              <Box ref={studiosFeed.sentinelRef} sx={{ py: 2, textAlign: "center" }}>
                <Button onClick={studiosFeed.loadMore} disabled={studiosFeed.loading}>
                  {getMessage(locale, "loadingContent")}
                </Button>
              </Box>
            )}
          </>
        )}

        {tab === 4 && (
          <>
            <SectionHeader
              icon={<VideocamIcon />}
              title={getMessage(locale, "homeMediaTitle")}
            />
            {mapMode && (
              <Box sx={{ mb: 2 }}>
                <DiscoveryMap
                  items={mapEntities.media}
                  getCoordinates={(media) => media.geo || media.venue?.geo || media.studio?.geo}
                  getLabel={(media) => media.title || media.name}
                  onSelect={(media) => onNavigate?.(`/media/${media.id}`)}
                  labelMessageKey="mediaMapLabel"
                  height={280}
                />
              </Box>
            )}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(mediaList ?? Array.from({ length: 4 })).map((media, idx) => (
                <Grid
                  size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  key={media?.id ?? idx}
                >
                  {media ? (
                    <MediaCard
                      media={media}
                      onView={(item) => onNavigate?.(`/media/${item.id}`)}
                    />
                  ) : (
                    <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
                  )}
                </Grid>
              ))}
            </Grid>
            {mediaFeed.hasMore && (
              <Box ref={mediaFeed.sentinelRef} sx={{ py: 2, textAlign: "center" }}>
                <Button onClick={mediaFeed.loadMore} disabled={mediaFeed.loading}>
                  {getMessage(locale, "loadingContent")}
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Guest CTA */}
        {!user && (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              mt: 4,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar>
              <FavoriteBorderIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={800}>Create an account</Typography>
              <Typography variant="body2" color="text.secondary">
                Follow artists & venues, get gig invites, message studios, and
                save events.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => onNavigate?.("/auth/signup")}
            >
              Get started
            </Button>
          </Paper>
        )}
      </Container>

      {/* Filters Drawer (calm, grouped) */}
      <Drawer
        anchor={isMdUp ? "right" : "bottom"}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { md: 360 },
            borderTopLeftRadius: { xs: 16, md: 0 },
            borderTopRightRadius: { xs: 16, md: 0 },
            p: 2,
            gap: 2,
          },
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>
            Filters
          </Typography>

          {tab === 0 && <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Date
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={dateFilter}
              onChange={(_, v) => v && setDateFilter(v)}
            >
              <ToggleButton value="Today">Today</ToggleButton>
              <ToggleButton value="Weekend">Weekend</ToggleButton>
              <ToggleButton value="All">All</ToggleButton>
            </ToggleButtonGroup>
          </Box>}

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Genres
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={genreOptions}
              value={genres.includes("All") ? [] : genres}
              onChange={(_, values) => {
                if (!values?.length || values.includes("All")) return setGenres(["All"]);
                setGenres(values);
              }}
              renderInput={(params) => <TextField {...params} placeholder="Select genres" />}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Radius: {radiusLabel(radius)}
            </Typography>
            <CommittedRadiusSlider value={radius} onChange={setRadius} />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={accessibleOnly}
                onChange={(e) => setAccessibleOnly(e.target.checked)}
              />
            }
            label="Accessible venues only"
          />

          <Stack direction="row" justifyContent="space-between" gap={1} sx={{ pt: 1 }}>
            <Button onClick={resetFilters}>Reset</Button>
            <Button
              variant="contained"
              onClick={() => setFiltersOpen(false)}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      {/* Bottom padding for safe area on mobile */}
      <Box sx={{ height: 16 }} />
    </Box>
  );
}
